import { Product, ProductPrice, ProductSlug } from "@/api/product-api";
import {
  OrderDraft,
  OrderDraftAddress,
  OrderItemV2,
} from "@/models/orders/OrderDraft";
import { Env } from "src/Env";
import {
  ProductCategory,
  AffiliationType,
  ItemBrandType,
} from "@/models/Product";
import { CurrencyIsoCode } from "src/enums";
import { CartItem } from "@/store/cart-store";
import { GameResell } from "@/models/GameResell";
import { GameItem } from "@/models/orders/OrderModelV2";
import { ProductUtil } from "@/utils/ProductUtil";

// https://developers.facebook.com/docs/meta-pixel/reference
export enum FbPixelEvent {
  // Page View
  PageView = "PageView", // 랜딩 페이지 진입시

  // View -> Cart -> Purchase Journey
  ViewContent = "ViewContent",
  AddToCart = "AddToCart",
  InitiateCheckout = "InitiateCheckout",
  AddPaymentInfo = "AddPaymentInfo", // GA에서 addShippingInfo와 매칭됨.
  Purchase = "Purchase",

  // Newsletter
  CompleteRegistration = "CompleteRegistration",
  SubmitApplication = "SubmitApplication",

  // Custom events
  RemoveFromCart = "RemoveFromCart",
  CustomsDutyAndVat = "CustomsDutyAndVat",
}

export interface FbPixelContent extends Required<{
  id: string;
  quantity: number;
}> {
  [key: string]: any;
}

export interface FbPixelViewContent {
  content_ids?: string[];
  content_category: "homepage" | "shop" | ProductCategory;
  content_name: string; // Added type for content_name
  content_type?: "product" | "product_group";
  contents?: FbPixelContent[];
  currency?: CurrencyIsoCode;
  value?: number;
}

export interface PixelItem {
  item_id: string; // item_id를 slug로 다 맞춤.
  item_name: string; // name이 있으면 넣고, 없으면 slug로 대체.
  item_sku: string; // sku 정보가 있으면 넣고, 없으면 slug로 대체.
  affiliation?: AffiliationType.Homepage;
  coupon?: string;
  currency?: CurrencyIsoCode;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  location_id?: string;
  price?: number;
  quantity?: number;
}

class FbPixel {
  static pageView = () => {
    FbPixel.track(FbPixelEvent.PageView);
  };

  static viewItem = ({
    product,
    currency,
    countryCode,
  }: {
    product: Product;
    currency: CurrencyIsoCode;
    countryCode: string | null;
  }) => {
    if (!product) return;

    // 표시·청구 통화 그대로 (#5190). Meta 도 GA4 와 같이 `value`+`currency` 정합을 요구하고,
    // 두 트래커가 다른 통화를 쓰면 Pixel↔CAPI dedup 이후의 ROAS 가 서로 안 맞는다.
    const price = this.getPrice(product, currency, countryCode);
    if (!price) return;

    FbPixel.track(
      FbPixelEvent.ViewContent,
      {
        content_ids: [product.slug], // purchase에서 sku(slug)로 사용되어서 GA에서 흐름이 이어지게 하기 위해 변경함.
        content_name: product.name,
        content_category: ProductCategory.Hardware,
        content_type: "product",
        currency: price.currency,
        value: price.originalAmount - price.discountAmount,
        num_items: 1,

        custom_data: {
          item_sku: product.slug,
          affiliation: AffiliationType.Homepage,
          item_brand: ItemBrandType.bHaptics,
          original_price: price?.originalAmount,
          discount_price: price?.discountAmount,
        },
      },
      crypto.randomUUID(),
    );
  };

  /**
   * 게임키 ViewContent. 통화 폴백 규칙은 `Analytics.gameResellToViewItems` 와 **같아야 한다**
   * — 한쪽만 폴백하면 같은 화면에서 GA4 는 무음인데 Meta 만 찍히는(또는 그 반대) 상태가 된다.
   */
  private static gameResellToViewContent = (
    gameResell: GameResell,
    currency: CurrencyIsoCode,
  ) => {
    const { id, name, platformList } = gameResell;

    const candidates = platformList
      .map((platform) => {
        const price =
          platform.priceList.find((p) => p.currency === currency) ??
          platform.priceList.find((p) => p.currency === CurrencyIsoCode.USD);
        if (!price) return null;
        const value = (price?.price ?? 0) - (price?.discount ?? 0);

        return {
          content_ids: [id],
          content_name: `${name} (${platform.platform})`,
          content_category: ProductCategory.Software,
          content_type: "product",
          // 요청 통화가 아니라 **실제로 고른 가격의 통화** (#4975 기전 회피).
          currency: price.currency,
          value,
          num_items: 1,
          custom_data: {
            item_sku: `${id}:${platform.platform}`,
            affiliation: AffiliationType.Homepage,
            item_brand: ItemBrandType.game,
            original_price: price?.price ?? 0,
            discount_price: price?.discount ?? 0,
          },
        };
      })
      .filter((item) => item !== null);

    return candidates[0];
  };

  static viewGameResell = (
    gameResell: GameResell | null | undefined,
    currency: CurrencyIsoCode,
  ) => {
    if (!gameResell) return;
    const content = FbPixel.gameResellToViewContent(gameResell, currency);
    if (!content) return;
    FbPixel.track(FbPixelEvent.ViewContent, content);
  };

  static addGameItem = ({
    gameItem,
    currency,
  }: {
    gameItem: GameItem | null;
    currency: CurrencyIsoCode;
  }) => {
    if (!gameItem || !currency) return;
    FbPixel.track(
      FbPixelEvent.AddToCart,
      this.gameItemToCartItem({ gameItem, currency }),
    );
  };

  static deleteGameItem = ({
    gameItem,
    currency,
  }: {
    gameItem: GameItem | null;
    currency: CurrencyIsoCode;
  }) => {
    if (!gameItem || !currency) return;
    FbPixel.track(
      FbPixelEvent.RemoveFromCart,
      this.gameItemToCartItem({ gameItem, currency }),
    );
  };

  static logCustomsDutyAndVat = (props: {
    shippingAmount: number;
    countryCode: string;
    shippingType: string;
    purchaseAmount: number;
    currency: CurrencyIsoCode;
  }) => {
    FbPixel.track(FbPixelEvent.CustomsDutyAndVat, {
      tax_type: "customs duty and vat",
      is_checked: true,
      country_code: props.countryCode,
      shipping_type: props.shippingType,
      purchase_amount: props.purchaseAmount,
      shipping_amount: props.shippingAmount,
      currency: props.currency,
    });
  };

  private static gameItemToCartItem = ({
    gameItem,
    currency,
  }: {
    gameItem: GameItem;
    currency: CurrencyIsoCode;
  }) => {
    const { name, gameResellId, platform, price, discount } = gameItem;
    return {
      content_ids: [gameResellId],
      content_name: `${name} (${platform})`,
      value: price - discount,
      content_category: ProductCategory.Software,
      content_type: "product",
      num_items: 1,
      currency,
      custom_data: {
        item_sku: `${gameResellId}:${platform}`,
        affiliation: AffiliationType.Homepage,
        item_brand: ItemBrandType.game,
        original_price: price,
        discount_price: discount,
      },
    };
  };

  static addItem = ({
    slug,
    quantity = 1,
    name,
    sku,
    value,
    currency,
  }: {
    slug: ProductSlug;
    quantity?: number;
    name?: string;
    sku: string;
    value?: number;
    currency?: CurrencyIsoCode;
  }) => {
    if (!slug) return;
    FbPixel.track(
      FbPixelEvent.AddToCart,
      {
        content_ids: [slug],
        content_name: name || slug,
        content_category: ProductCategory.Hardware,
        content_type: "product",
        num_items: quantity,
        ...(value != null && { value }),
        ...(currency && { currency }),
        custom_data: {
          item_sku: sku,
          affiliation: AffiliationType.Homepage,
          item_brand: ItemBrandType.bHaptics,
        },
      },
      crypto.randomUUID(),
    );
  };

  static removeItem = ({
    slug,
    sku,
    quantity,
    name,
    value,
    currency,
  }: {
    slug: ProductSlug;
    sku: string;
    quantity: number;
    name?: string;
    value?: number;
    currency?: CurrencyIsoCode;
  }) => {
    if (!slug) return;

    FbPixel.track(FbPixelEvent.RemoveFromCart, {
      content_ids: [slug],
      content_name: name || slug,
      content_category: ProductCategory.Hardware,
      content_type: "product",
      num_items: quantity,
      ...(value != null && { value }),
      ...(currency && { currency }),
      custom_data: {
        item_sku: sku,
        affiliation: AffiliationType.Homepage,
        item_brand: ItemBrandType.bHaptics,
      },
    });
  };

  static initCheckout = (
    cartItems: CartItem[],
    gameItems: GameItem[],
    totalPayable: number, // 할인을 적용한 후 최종 결제해야 하는 금액입니다.
    // ★`totalPayable` 이 계산된 통화를 그대로 받는다. 과거엔 countryCode 로 재도출했는데
    // EUR/GBP 가 매핑에 없어 **금액은 EUR 인데 라벨만 USD** 로 나갔다(리뷰 §2). value 와
    // currency 는 한 몸이므로 값을 만든 쪽이 통화도 넘기는 게 유일하게 안전한 형태다.
    // (통화를 받게 되면서 `countryCode` 파라미터는 쓰이지 않아 제거했다.)
    currency: CurrencyIsoCode,
  ) => {
    const content_ids = [
      ...cartItems.map((item) => item.slug),
      ...gameItems.map((item) => item.id),
    ];
    const contents = [
      ...cartItems.map((item) => ({
        id: item.slug,
        quantity: item.quantity,
        content_name: item.slug,
        item_sku: item.sku,
        affiliation: AffiliationType.Homepage,
        item_brand: ItemBrandType.bHaptics,
      })),
      ...gameItems.map((item) => ({
        id: item.gameResellId,
        quantity: 1,
        content_name: `${item.name} (${item.platform})`,
        item_sku: `${item.gameResellId}:${item.platform}`,
        affiliation: AffiliationType.Homepage,
        item_brand: ItemBrandType.game,
      })),
    ];

    FbPixel.track(
      FbPixelEvent.InitiateCheckout,
      {
        value: totalPayable,
        currency,
        content_ids,
        contents,
        content_type: content_ids.length > 1 ? "product_group" : "product",
      },
      crypto.randomUUID(),
    );
  };

  static addPaymentInfo = (order: OrderDraft) => {
    FbPixel.setAdvancedMatching(order.shippingAddress);

    const {
      id,
      items,
      gameItems,
      currency,
      shippingCountry,
      shippingPrice,
      subtotalAmount,
      referralCode,
    } = order;
    const value = subtotalAmount;
    const content_ids = FbPixel.orderItemsToPixelContentIds(items, gameItems);
    const contents = FbPixel.orderItemsToPixelContents(
      items,
      gameItems,
      currency,
    );

    FbPixel.track(
      FbPixelEvent.AddPaymentInfo,
      {
        value,
        currency,
        content_type: content_ids.length > 1 ? "product_group" : "product",
        content_ids,
        contents,

        // Additional custom properties
        order_draft_id: id,
        shipping_country: shippingCountry,
        shipping_price: shippingPrice,
        referral_code: referralCode,
      },
      id,
    );
  };

  // Ref: https://developers.facebook.com/docs/meta-pixel/implementation/conversion-tracking/#parameters
  static purchased = (order: {
    id: string;
    items: OrderItemV2[];
    gameItems: GameItem[];
    currency: CurrencyIsoCode;
    shippingCountry: string;
    subtotalAmount: number;
    shippingPrice: number;
    referralCode: string;
  }) => {
    const {
      id,
      items,
      gameItems,
      currency,
      shippingCountry,
      subtotalAmount,
      shippingPrice,
      referralCode,
    } = order;
    const value = subtotalAmount;
    const content_ids = FbPixel.orderItemsToPixelContentIds(items, gameItems);
    const contents = FbPixel.orderItemsToPixelContents(
      items,
      gameItems,
      currency,
    );

    FbPixel.track(
      FbPixelEvent.Purchase,
      {
        value,
        currency,
        content_type: content_ids.length > 1 ? "product_group" : "product",
        content_ids,
        contents,

        // Additional custom properties
        order_draft_id: id,
        shipping_country: shippingCountry,
        shipping_price: shippingPrice,
        referral_code: referralCode,
      },
      id,
    );
  };

  static completeRegistration = () => {
    FbPixel.track(FbPixelEvent.CompleteRegistration);
  };

  private static setAdvancedMatching = (address: OrderDraftAddress) => {
    if (typeof window === "undefined" || !("fbq" in window)) return;

    const userData: Record<string, string> = {};
    if (address.email) userData.em = address.email.toLowerCase().trim();
    if (address.tel) userData.ph = address.tel.replace(/\D/g, "");
    if (address.firstName) userData.fn = address.firstName.toLowerCase().trim();
    if (address.lastName) userData.ln = address.lastName.toLowerCase().trim();
    if (address.city) userData.ct = address.city.toLowerCase().trim();
    if (address.state) userData.st = address.state.toLowerCase().trim();
    if (address.zip) userData.zp = address.zip.trim();

    if (Object.keys(userData).length > 0) {
      // @ts-ignore
      window.fbq("init", Env.FacebookPixelId, userData);
    }
  };

  // 서버사이드 Meta CAPI로도 전송할 이벤트
  private static SERVER_SIDE_EVENTS = new Set([
    FbPixelEvent.ViewContent,
    FbPixelEvent.AddToCart,
    FbPixelEvent.InitiateCheckout,
    FbPixelEvent.AddPaymentInfo,
  ]);

  private static track = (
    event: FbPixelEvent,
    param: any = null,
    eventID?: string,
  ) => {
    if (typeof window !== "undefined" && "fbq" in window) {
      const options = eventID ? { eventID } : undefined;

      if (param) {
        // @ts-ignore
        window.fbq("track", event, param, options);
      } else {
        // @ts-ignore
        window.fbq("track", event, {}, options);
      }
    }

    // 서버사이드 Meta CAPI 전송 (eventID가 있는 주요 이벤트만)
    if (eventID && FbPixel.SERVER_SIDE_EVENTS.has(event)) {
      FbPixel.sendServerSide(event, eventID, param);
    }
  };

  private static sendServerSide = (
    event: FbPixelEvent,
    eventID: string,
    customData?: any,
  ) => {
    if (typeof window === "undefined") return;

    fetch("/api/meta-event/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_name: event,
        event_id: eventID,
        event_source_url: window.location.href,
        custom_data: customData,
      }),
    }).catch(() => {});
  };

  private static orderItemsToPixelContentIds = (
    items: OrderItemV2[],
    gameItems: GameItem[] = [],
  ): string[] => {
    return [
      ...items.map((item) => item.slug),
      ...gameItems.map((item) => item.gameResellId),
    ];
  };

  private static orderItemsToPixelContents = (
    items: OrderItemV2[],
    gameItems: GameItem[] = [],
    currency: CurrencyIsoCode,
  ): FbPixelContent[] => {
    return [
      ...items.map((item) => ({
        id: item.slug, // Required
        quantity: item.qty, // Required
        item_price: item.price,
        content_name: item.slug,
        item_sku: item.sku,
        currency: currency,
        discount: item.discount,
        affiliation: AffiliationType.Homepage,
        item_brand: ItemBrandType.bHaptics,
        item_variant: JSON.stringify(item.options),
      })),
      ...gameItems.map((item) => ({
        id: item.gameResellId, // Required
        quantity: 1, // Required
        item_price: item.price,
        content_name: `${item.name} (${item.platform})`,
        item_sku: `${item.gameResellId}:${item.platform}`,
        currency,
        discount: item.discount,
        affiliation: AffiliationType.Homepage,
        item_brand: ItemBrandType.game,
      })),
    ];
  };

  /** 표시·청구 통화 그대로 집는다 (#5190). `Analytics.getPrice` 와 같은 호출이어야 한다. */
  private static getPrice = (
    product: Product,
    currency: CurrencyIsoCode,
    countryCode: string | null,
  ): ProductPrice | null => {
    return ProductUtil.getProductPrice({
      currency,
      countryCode,
      product,
    });
  };
}

export default FbPixel;
